import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ProfileCombo from '../../sharedComponents/ProfileCombo';
import useGetCustomerProfile from '../../hooks/useGetCustomerProfile';
import { formatDate } from '../../utils/dateUtils';
import CaseTicketService from './CaseTicketService';
import DiagnosisChip from './DiagnosisChip';
import { RESOLVED_ON_THE_SPOT, NEEDS_ADMIN_FIRST } from './constants';

// "ของยังไม่ถึง" — the first thing the admin does is LOOK, not open a case.
// Pick the customer → every recent order with where the system thinks it is.
// Most chases end here ("อยู่กล่อง 12 รอบ 224 ถึงศุกร์นี้"). Only the parcels the
// check can't answer get ticked and handed to the warehouse — that hand-off is
// what creates the case (North's rule: no case until the warehouse is asked).
const QuickCheckPanel = ({ onOpenCase }) => {
  const { profiles, getCustomerProfiles } = useGetCustomerProfile();
  const [customer, setCustomer] = useState(null);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => { getCustomerProfiles(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCustomerSelect = async (smileName) => {
    const matched = (profiles || []).find((p) => p.smileName === smileName);
    if (!matched) return;
    setCustomer(matched);
    setLoading(true);
    try {
      const res = await CaseTicketService.quickCheck(matched.id);
      const orders = res.data.orders || [];
      setRows(orders);
      // Pre-tick what the warehouse can actually be asked about — not the ones
      // still waiting on an admin to find a tracking number.
      const next = {};
      orders.forEach((o) => {
        const f = o.diagnosis?.flag;
        if (!RESOLVED_ON_THE_SPOT.includes(f) && !NEEDS_ADMIN_FIRST.includes(f)) next[o.id] = true;
      });
      setSelected(next);
    } catch (err) {
      toast.error(err.response?.data?.message || 'เช็คไม่สำเร็จ');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // The admin confirms there is genuinely no number — the order gets the
  // canonical ไม่มีเลข, and the row becomes something the warehouse can hunt.
  const handleNoTracking = async (row) => {
    if (!window.confirm(`ยืนยันว่าหาเลขพัสดุไม่เจอจริงๆ?\n${row.url || ''}\nระบบจะบันทึกเป็น "ไม่มีเลข" ที่ออเดอร์นี้`)) return;
    try {
      const res = await CaseTicketService.markNoTracking(row.id);
      setRows((prev) => prev.map((r) => (r.id === row.id
        ? { ...r, trackingJp: res.data.trackingJp, diagnosis: res.data.diagnosis } : r)));
      setSelected((prev) => ({ ...prev, [row.id]: true }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'บันทึกไม่สำเร็จ');
    }
  };

  const chosen = rows.filter((r) => selected[r.id]);
  // A row the admin cannot tick — already found, or still needs a tracking
  // number typed on the order — must not be swept in by "tick everything".
  const selectable = rows.filter((r) => !RESOLVED_ON_THE_SPOT.includes(r.diagnosis?.flag)
    && !NEEDS_ADMIN_FIRST.includes(r.diagnosis?.flag));
  const allChosen = selectable.length > 0 && selectable.every((r) => selected[r.id]);
  const toggleAll = () => setSelected(allChosen ? {} : Object.fromEntries(selectable.map((r) => [r.id, true])));
  const unresolved = rows.filter((r) => !RESOLVED_ON_THE_SPOT.includes(r.diagnosis?.flag));
  const adminFirst = rows.filter((r) => NEEDS_ADMIN_FIRST.includes(r.diagnosis?.flag));

  const handleOpenCase = () => {
    if (!customer || chosen.length === 0) { toast.error('ติ๊กรายการที่ยังหาไม่เจอก่อน'); return; }
    // The round is never asked for — the backend reads it off these orders.
    onOpenCase({
      customerId: customer.id,
      customerName: customer.contact || customer.smileName,
      items: chosen.map((r) => ({
        orderId: r.id, url: r.url, trackingJp: r.trackingJp, priceYen: r.amount, orderDate: r.orderDate,
        isSendOnly: !!r.isSendOnly,
      })),
    });
  };

  return (
    <div className="border border-sky-200 bg-sky-50 rounded-lg p-4 mb-4">
      <h3 className="font-semibold text-sky-900 mb-3">
        🔍 เช็คของลูกค้า <span className="text-sm font-normal text-sky-700">— ดูก่อนว่าของอยู่ไหน ยังไม่เปิดเคส</span>
      </h3>

      {/* Always by customer: they ask about one parcel, and seeing all of them
          lets the admin answer that one AND get ahead of the others. */}
      <div className="mb-3">
        <p className="text-xs font-medium text-sky-800 mb-1">ค้นหาจากชื่อลูกค้า — เห็นทุกออเดอร์</p>
        <ProfileCombo profiles={profiles || []} onChange={handleCustomerSelect} initialSmileName={customer?.smileName || ''} />
      </div>

      {loading && <p className="text-sm text-gray-500">กำลังเช็ค...</p>}

      {!loading && customer && rows.length === 0 && (
        <p className="text-sm text-gray-500">ไม่มีออเดอร์ใน 120 วันล่าสุด</p>
      )}

      {rows.length > 0 && (
        <>
          <table className="w-full text-sm bg-white rounded border border-sky-100">
            <thead className="bg-sky-100 text-sky-900 text-xs">
              <tr>
                <th className="p-2 w-8 text-center">
                  <input type="checkbox" title={allChosen ? 'เอาออกทั้งหมด' : 'เลือกทั้งหมด'}
                    checked={allChosen} disabled={selectable.length === 0} onChange={toggleAll}
                    ref={(el) => { if (el) el.indeterminate = !allChosen && selectable.some((r) => selected[r.id]); }} />
                </th>
                <th className="p-2 text-left">ออเดอร์</th>
                <th className="p-2 text-left">วันสั่ง</th>
                <th className="p-2 text-left">เลขพัสดุ</th>
                <th className="p-2 text-left">ระบบเจอที่ไหน</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const resolved = RESOLVED_ON_THE_SPOT.includes(r.diagnosis?.flag);
                const needsMe = NEEDS_ADMIN_FIRST.includes(r.diagnosis?.flag);
                return (
                  <tr key={r.id} className={`border-t border-sky-50 ${resolved ? 'text-gray-500' : ''}`}>
                    <td className="p-2 text-center">
                      <input type="checkbox" checked={!!selected[r.id]} disabled={resolved || needsMe}
                        onChange={(e) => setSelected((prev) => ({ ...prev, [r.id]: e.target.checked }))} />
                    </td>
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        {r.imageUrl && <img src={r.imageUrl} alt="" className="w-8 h-8 object-cover rounded" />}
                        <a href={r.url} target="_blank" rel="noreferrer" className="text-blue-700 underline truncate max-w-xs block">
                          {r.url} · {r.amount} เยน
                        </a>
                      </div>
                    </td>
                    <td className="p-2 whitespace-nowrap">{r.orderDate ? formatDate(r.orderDate) : '—'}</td>
                    <td className="p-2 font-mono text-xs">{r.trackingJp || <span className="text-gray-300">—</span>}</td>
                    <td className="p-2">
                      <DiagnosisChip diagnosis={r.diagnosis} />
                      {needsMe && (
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <span className="text-xs text-gray-500">ไปหาเลขพัสดุที่หน้าออเดอร์ก่อน หรือ</span>
                          <button onClick={() => handleNoTracking(r)}
                            className="text-xs border border-red-300 text-red-700 bg-white hover:bg-red-50 px-2 py-0.5 rounded">
                            ไม่มีเลข — หาไม่เจอ
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
            <span className="text-sm text-gray-600">
              ยืนยันแล้ว {rows.length - unresolved.length} · ยังไม่ยืนยัน <b>{unresolved.length}</b> รายการ
              {adminFirst.length > 0 && <> · <b className="text-gray-700">{adminFirst.length}</b> รายการต้องหาเลขพัสดุก่อน</>}
              <span className="block text-xs text-gray-400">"ยังไม่เจอในระบบ" ≠ หาย — ชีทโกดังอัปโหลดด้วยมือ อาจช้า 1–2 วัน</span>
            </span>
            <button onClick={handleOpenCase} disabled={chosen.length === 0}
              className="bg-orange-600 hover:bg-orange-700 disabled:opacity-40 text-white px-4 py-2 rounded-lg font-medium">
              📦 ส่งให้ Kluay เช็ค — เปิดเคสตามของ ({chosen.length})
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default QuickCheckPanel;
